<template>
  <div class="flex flex-col gap-4 -mt-8">
    <h1 class="text-2xl font-medium text-text-primary">{{ $t('common.overview') }}</h1>
    <div class="gap-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      <!-- Highlights -->
      <div v-if="orderedProgress?.length > 0" class="flex flex-col bg-gray-900 rounded-lg overflow-hidden flex-1 divide-y divide-gray-950 col-span-1">
        <div class="flex flex-col gap-2 p-6 flex-1 green-gradient">
          <h1 class="text-lg font-medium text-text-primary flex-1 flex gap-2 items-start">
            <div class="flex gap-2 items-center">
              {{ $t('coach.overview.highlights') }}
              <div class="has-tooltip">
                <img src="../../../assets/icons/info-icon.svg" class="w-4 h-4">
                <span class="tooltip rounded shadow-md text-left p-3 bg-gray-800 text-white text-xs -mt-8 ml-5">
                  {{ $t('coach.overview.highlights_tooltip') }}
                </span>
              </div>
            </div>
          </h1>
          <p class="text-text-secondary text-sm font-light">
            <i18n-t scope="global" keypath="coach.overview.improved" tag="span">
              <template #first><span class="text-text-primary">{{ formatStatString(orderedProgress[0]?.title) }}</span></template>
              <template #first_delta>
                <span class="text-success-400">
                  <span v-if="orderedProgress[0]?.is_ascending">+</span><span v-else>-</span>
                  <span>{{ formatDataString(parseFloat(orderedProgress[0]?.improvement), orderedProgress[0]?.unit_type) }}</span>
                </span>
              </template>
              <template #second><span class="text-text-primary">{{ formatStatString(orderedProgress[1]?.title) }}</span></template>
              <template #second_delta>
                <span class="text-success-400">
                  <span v-if="orderedProgress[1]?.is_ascending">+</span><span v-else>-</span>
                  <span>{{ formatDataString(parseFloat(orderedProgress[1]?.improvement), orderedProgress[1]?.unit_type) }}</span>
                </span>
              </template>
            </i18n-t>
            <br /><br />
            <span>{{ $t('coach.overview.great_job') }}</span>
          </p>
        </div>
        <div v-for="item in orderedProgress?.slice(0, 2)" :key="item.title" class="flex gap-2 px-6 py-4 items-center">
          <h1 class="text-text-secondary flex-1">{{ toTitleCase(formatStatString(item.title)) }}</h1>
          <p class="text-success-400 text-xs">
            {{ item.is_ascending ? '+' : '-' }}{{ formatDataString(parseFloat(item.improvement), item.unit_type) }}
          </p>
          <p class="text-text-primary text-lg">{{ formatDataString(item.value, item.unit_type) }}</p>
        </div>
      </div>
      <div v-else class="flex flex-col bg-gray-900 rounded-lg overflow-hidden flex-1 divide-y divide-gray-950 col-span-1">
        <div class="flex flex-col gap-2 p-6 flex-1 green-gradient">
          <h1 class="text-lg font-medium text-text-primary flex gap-2 flex-1">
            {{ $t('coach.overview.highlights') }}
            <div class="has-tooltip">
              <img src="../../../assets/icons/info-icon.svg" class="w-4 h-4">
              <span class="tooltip rounded shadow-md text-left p-3 bg-gray-800 text-white text-xs -mt-8 ml-5">
                {{ $t('coach.overview.highlights_tooltip') }}
              </span>
            </div>
          </h1>
          <p class="text-text-secondary text-sm font-light">{{ $t('coach.overview.no_progress') }}</p>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="flex flex-col bg-gray-900 rounded-lg flex-1 divide-y divide-gray-950 col-span-1">
        <div class="flex gap-2 p-6 flex-1">
          <div class="flex flex-col gap-1 flex-1">
            <h1 class="text-sm text-text-secondary">{{ $t('common.matches') }}</h1>
            <p class="text-text-primary">{{ extraStats?.match_count ?? 0 }}</p>
          </div>
          <div class="flex flex-col gap-1 flex-1">
            <h1 class="text-sm text-text-secondary">{{ $t('coach.overview.win_rate') }}</h1>
            <p class="text-text-primary">{{ extraStats?.win_rate ? (extraStats.win_rate * 100).toFixed(0) : 0 }}%</p>
          </div>
          <div class="flex flex-col gap-1 flex-1">
            <h1 class="text-sm text-text-secondary whitespace-nowrap">{{ $t('coach.overview.recent_results') }}</h1>
            <div class="flex gap-1">
              <h1
                v-for="(match, index) in [...(matchHistory?.slice(0, 5) || [])].reverse()"
                :key="index"
                :class="match.match_result === 1 ? 'text-success-400' : match.match_result === 2 ? 'text-text-secondary' : 'text-danger-400'"
              >{{ match.match_result === 1 ? $t('coach.overview.result_win') : match.match_result === 2 ? $t('coach.overview.result_tie') : $t('coach.overview.result_loss') }}</h1>
            </div>
          </div>
        </div>
        <div class="flex gap-2 px-6 py-4 items-center">
          <h1 class="text-text-secondary flex-1">{{ $t('coach.stat_labels.kdr') }}</h1>
          <p class="text-text-primary text-lg">
            <!-- Show the stored kdr so the cell matches the breakdown popover's header -->
            <StatBreakdown stat-key="kdr" source="profile" :values="profileStats">
              {{ formatStat('kdr') }}
            </StatBreakdown>
          </p>
        </div>
        <div class="flex gap-2 px-6 py-4 items-center">
          <h1 class="text-text-secondary flex-1">{{ $t('coach.stat_labels.headshot_percentage') }}</h1>
          <p class="text-text-primary text-lg">
            <StatBreakdown stat-key="headshot_kill_percentage" source="profile" :values="profileStats">
              {{ formatStat('headshot_kill_percentage') }}
            </StatBreakdown>
          </p>
        </div>
        <div class="flex gap-2 px-6 py-4 items-center">
          <h1 class="text-text-secondary flex-1">ADR</h1>
          <p class="text-text-primary text-lg">
            <StatBreakdown stat-key="adr" source="profile" :values="profileStats">
              {{ formatStat('adr') }}
            </StatBreakdown>
          </p>
        </div>
      </div>

      <!-- Routine -->
      <Routine :routines="dailyRoutines" :user="user" @launchRoutine="$emit('launchRoutine', $event)" @refreshRoutines="$emit('refreshRoutines')" class="h-auto flex col-span-1 md:col-span-2 xl:col-span-1" />
    </div>
  </div>
</template>

<script setup>
import Routine from '../Routine.vue';
import StatBreakdown from '../StatBreakdown.vue';
import {
  formatStatString, toTitleCase, formatDataString, formatBreakdownValue,
} from '../../../utils/formatters';
import { getUnitType } from '../../../composables/useCoachData.js';
import { resolveBreakdown } from '../../../composables/useStatBreakdown.js';

const props = defineProps({
  orderedProgress: Array,
  extraStats: Object,
  matchHistory: Array,
  profileStats: Object,
  dailyRoutines: Array,
  user: Object,
});

defineEmits(['launchRoutine', 'refreshRoutines']);

// Reads the value, unit type and missing-value fallback off the same breakdown the
// popover renders, so the trigger and the popover header cannot disagree.
function formatStat(key) {
  const stats = props.profileStats;
  const breakdown = stats ? resolveBreakdown(key, { source: 'profile', values: stats }) : null;
  const value = breakdown?.displayValue ?? stats?.[key]?.players_value;
  if (value == null) return '\u2014';
  return formatBreakdownValue(value, breakdown?.unit_type ?? stats?.[key]?.unit_type ?? getUnitType(key));
}
</script>

<style scoped>
.green-gradient {
  background: radial-gradient(51.81% 50% at 51.81% 0%, rgba(37, 229, 206, 0.20) 0%, rgba(37, 229, 206, 0.00) 100%), var(--Gray-900, #0F141A);
}
</style>
